import { useEffect } from "react";
import { usePaymentStore } from "../../stores/payment";
import { Select } from "../ui/inputs/Select";
import { showToastError, showToastSuccess } from "../../services/toast";
import { Input } from "../ui/inputs/Input";
import { getBanks } from "../../api/banks";
import TableInvoices from "./TableInvoices";
import { useButtonStore } from "../../stores/components/button";
import {
  createPayment,
  getPayment,
  getPaymentMethods,
  updatePayment,
} from "../../api/payments";
import { SelectText } from "../ui/inputs/SelectText";
import { getCustomerFiles } from "../../api/customerFile";
import PaymentDifference from "./PaymentDifference";
import { formatDateForInput, parseAmountInput } from "../../services/function";
import { TextArea } from "../ui/inputs/TextArea";
import { useInvoiceStore } from "../../stores/invoice";
import { ActionButton, DetailHeader } from "../ui/page/PageShell";

const PaymentWithInvoices = () => {
  const {
    tabPayment,
    viewOrUpdate,
    formData,
    setFormData,
    clientFiles,
    setClientFiles,
    banks,
    setBanks,
    methods,
    setMethods,
    setViewOrUpdate,
    idPayment,
    idsInvoicesSelected,
    setIdsInvoicesSelected,
    openPaiementDifference,
    setOpenPaiementDifference,
    resetPayment,
    updatePaymentBool,
    setUpdatePaymentBool,
    balance,
    setBalance,
    setDifference,
    setAppliedAmounts,
    setInitialised,
    trueAmount,
  } = usePaymentStore();
  const { setInvoices } = useInvoiceStore();
  const { setCreateButton } = useButtonStore();

  useEffect(() => {
    if (idPayment !== 0 && viewOrUpdate === "update") {
      getPayment(idPayment)
        .then((res) => {
          const data = res.data;
          setInitialised(true);
          // Récupération des factures sélectionnées
          setIdsInvoicesSelected(data.invoices.map((invoice: any) => ({ 
            id: invoice.id, 
            balance: invoice.balance, 
            totalAmount: invoice.totalAmount, 
            amountApplied: invoice.amountApplied,
            invoiceNumber: invoice.invoiceNumber,
            MAEFA: invoice.amountApplied ?? 0, 
            SAF: invoice.balance ?? 0
          })));

          // Formatage des dates pour affichage dans les inputs
          data.date = formatDateForInput(data.date);
          setBalance(data.balance);
          setFormData(data);
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, [idPayment, viewOrUpdate]);

  // Récupère les modèles de documents et les dossiers clients
  useEffect(() => {
    getCustomerFiles()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));

        setClientFiles(formattedData);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });

    getBanks()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));

        setBanks(formattedData);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });

    getPaymentMethods()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));

        setMethods(formattedData);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  }, [updatePaymentBool]);

  // Gestion des changements de formulaire
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };

  const handleSelectTextChange = (selectedOption: any) => {
    if (selectedOption) { 
      setFormData({ ...formData, idCustomerFile: selectedOption.value });
    } else {
      setFormData({ ...formData, idCustomerFile: null });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Gestion de la création d'un encaissement / modification d'un encaissement
  const handlePayment = () => {
    if (formData.amount === "" || formData.amount === null || formData.amount === undefined) {
      showToastError("Le montant TTC est requis");
      return;
    }
    if(formData.idBank === "" || formData.idBank === null || formData.idBank === undefined) {
      showToastError("La banque d'encaissement est requise");
      return;
    }
    if(formData.idPaymentMethod === "" || formData.idPaymentMethod === null || formData.idPaymentMethod === undefined) {
      showToastError("Le mode de paiement est requis");
      return;
    }
    if(formData.idCustomerFile === "" || formData.idCustomerFile === null || formData.idCustomerFile === undefined) {
      showToastError("Le dossier client est requis");
      return;
    }
    if(formData.date === "" || formData.date === null || formData.date === undefined) {
      showToastError("La date d'encaissement est requise");
      return;
    }
    const parsedAmount = parseAmountInput(formData.amount);
    if (!Number.isFinite(parsedAmount)) {
      showToastError("Montant TTC invalide");
      return;
    }

    if (trueAmount !== 0) {
      setOpenPaiementDifference(!openPaiementDifference);
      setDifference(Number(balance) || 0);
      return;
    } else {
      if (viewOrUpdate === "") {
        const data = {
          ...formData,
          amount: parseFloat(parsedAmount.toFixed(2)),
          createAPaymentDifference: false,
          // Ajout des montants appliqués pour chaque facture
          invoices: idsInvoicesSelected.map((invoice: any) => {
            return {
              id: invoice.id,
              amountApplied: invoice.MAEFA,
            }
          }),
        };
        createPayment(data)
          .then(() => {
            setCreateButton(false);
            setViewOrUpdate("");
            showToastSuccess("Encaissement créé");
            setUpdatePaymentBool(!updatePaymentBool);
            setFormData({})
            resetPayment();
          })
          .catch(() => {
            showToastError("Une erreur est survenue");
          });
      } else {
        const data = {
          ...formData,
          createAPaymentDifference: false,
          // Ajout des montants appliqués pour chaque facture
          invoices: idsInvoicesSelected.map((invoice: any) => {
            return {
              id: invoice.id,
              amountApplied: invoice.MAEFA,
            }
          }),
        };
        updatePayment(idPayment, data)
          .then(() => {
            setCreateButton(false);
            setViewOrUpdate("");
            setFormData({})
            setUpdatePaymentBool(!updatePaymentBool);
            showToastSuccess("Encaissement mis à jour");
          })
          .catch(() => {
            showToastError("Une erreur est survenue");
          });
      }
    }
  };

  // Mise à jour des factures sélectionnées lors du changement de dossier client
  useEffect(() => {
    if(viewOrUpdate !== "update") {
    setIdsInvoicesSelected([])
    setInvoices([])
    setAppliedAmounts({})
    }
  }, [formData.idCustomerFile]);

  useEffect(() => {
    if(viewOrUpdate !== "update") {
      setIdsInvoicesSelected([]);
    }
  }, [formData.amount]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <DetailHeader
        title={
          tabPayment === "Encaissements" && viewOrUpdate === ""
            ? "Création d'un encaissement"
            : tabPayment === "Encaissements" && viewOrUpdate !== ""
            ? "Modification d'un encaissement"
            : null
        }
        hint="Merci de renseigner les informations nécessaires pour la création de l'encaissement."
        onClose={() => {
          setCreateButton(false);
          setViewOrUpdate("");
          resetPayment();
        }}
        actions={
          <ActionButton variant="primary" onClick={handlePayment}>
            {viewOrUpdate === "" ? "Créer" : "Modifier"}
          </ActionButton>
        }
      />

      {openPaiementDifference && (
        <PaymentDifference
          closeModal={() => setOpenPaiementDifference(!openPaiementDifference)}
        />
      )}

      <div className="flex min-h-0 w-full flex-1 items-start overflow-hidden p-3 sm:p-4">
        <form
          className="flex h-full min-h-0 w-full items-start justify-start gap-3 overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            handlePayment();
          }}
        >
          <div className="flex h-full min-h-0 w-2/5 flex-col items-center overflow-y-auto rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex justify-start items-start w-full ">
              <SelectText
                label="Dossier"
                name="idCustomerFile"
                onChange={handleSelectTextChange}
                value={formData.idCustomerFile}
                fetchOptions={clientFiles}
                disabled={viewOrUpdate !== ""}
                className="cursor-pointer w-1/2 p-2"
              />
              <Input
                label="Date d'encaissement"
                name="date"
                required
                type="date"
                disabled={viewOrUpdate !== ""}
                value={formData.date || ""}
                onChange={handleChange}
                clasName="w-44 mt-2"
              />
            </div>
            <div className="flex justify-start items-start w-full ">
              <Select
                label="Banque encaissement"
                name="idBank"
                disabled={viewOrUpdate !== ""}
                required={true}
                value={formData.idBank || ""}
                onChange={handleSelectChange}
                fetchOptions={banks}
                className={`disabled:opacity-50 disabled:text-grey-200 cursor-pointer w-full p-2`}
              />
              <Select
                label="Mode de paiement"
                name="idPaymentMethod"
                disabled={viewOrUpdate !== ""}
                required={true}
                value={formData.idPaymentMethod || ""}
                onChange={handleSelectChange}
                fetchOptions={methods}
                className={`disabled:opacity-50 disabled:text-grey-200 cursor-pointer w-full p-2`}
              />
            </div>
            <div className="flex justify-start items-start w-full ">
              <Input
                label="Banque d'émission"
                name="issueBank"
                value={formData.issueBank || ""}
                disabled={viewOrUpdate !== ""}
                onChange={handleChange}
                clasName={`w-1/2 p-2 cursor-pointer ${
                  viewOrUpdate !== "" ? "opacity-50 text-grey-200" : ""
                }`}
              />
            </div>
              <TextArea
                label="Commentaire"
                name="commentary"
                value={formData.commentary || ""}
                onChange={handleTextAreaChange}
                disabled={viewOrUpdate !== ""}
                clasName={`w-full p-2 cursor-pointer ${
                  viewOrUpdate !== "" ? "opacity-50 text-grey-200" : ""
                }`}
              />
            <Input
              label="Montant TTC"
              name="amount"
              type="text"
              required
              value={formData.amount || ""}
              disabled={viewOrUpdate !== ""}
              onChange={handleChange}
              clasName={`w-1/2 p-2 cursor-pointer mt-5 ${
                viewOrUpdate !== "" ? "opacity-50 text-grey-200" : ""
              }`}
            />
            {viewOrUpdate === "update" && <p className={`opacity-50 text-center font-thin text-grey-200 cursor-pointer w-full p-2`}>Solde restant : {balance?.toFixed(2)} €</p>}
            
            {/* Résumé de la répartition */}
              <div className="mt-3 w-full rounded-xl bg-indigo-50 p-3 ring-1 ring-indigo-100 ring-inset">
                <p className="mb-1.5 text-[11px] font-bold tracking-wider text-indigo-700 uppercase">
                  Répartition automatique
                </p>
                <div className="font-Nunito text-xs text-indigo-900">
                  {idsInvoicesSelected?.length > 0 ? idsInvoicesSelected?.map((invoice: any) => {
                    return (
                      <div key={invoice?.id} className="flex justify-between">
                        <span>Facture {invoice?.invoiceNumber}:</span>
                        <span className="font-semibold">{invoice?.MAEFA.toFixed(2)} €</span>
                      </div>
                    )
                  }) : null}
                  <div className="mt-1.5 border-t border-indigo-200 pt-1.5 font-bold">
                    <div className="flex justify-between">
                      <span>Total appliqué</span>
                      <span className="tabular-nums">{idsInvoicesSelected?.length > 0 ? idsInvoicesSelected?.reduce((sum: number, invoice: any) => sum + invoice?.MAEFA, 0).toFixed(2) : 0} €</span>
                    </div>
                  </div>
                </div>
              </div>
          </div>
          <div className="flex h-full min-h-0 w-3/5 flex-col overflow-y-auto rounded-xl border border-slate-200 bg-white">
            <TableInvoices />
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentWithInvoices;
